import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, FormControlLabel, Switch } from '@mui/material';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (formulaId: string, priceRatio: number, linkedRatioFormulaId?: string) => void;
  baseFormulaId: string;
  existingDerivedIds: string[];
}

export const AddDerivedMaterialModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  baseFormulaId,
  existingDerivedIds,
}) => {
  const { materialsMap } = useMaterialsStore();
  const { data: formulasPricesData } = useFormulasPrices();
  const [selectedFormulaId, setSelectedFormulaId] = React.useState<string>('');
  const [priceRatio, setPriceRatio] = React.useState<string>('1.0');
  const [useRatioMaterial, setUseRatioMaterial] = React.useState<boolean>(false);
  const [selectedRatioMaterialId, setSelectedRatioMaterialId] = React.useState<string>('');

  // Get available materials (exclude base and already derived)
  const availableMaterials = React.useMemo(() => {
    return Object.values(materialsMap).filter(
      (material) =>
        material.id !== baseFormulaId &&
        !existingDerivedIds.includes(material.id)
    );
  }, [materialsMap, baseFormulaId, existingDerivedIds]);

  // Get ratio materials (materials with contractType === 'ratio')
  const ratioMaterials = React.useMemo(() => {
    const allMaterials = Object.values(materialsMap);
    console.log('[AddDerivedMaterialModal] All materials:', allMaterials.map(m => ({ 
      id: m.id, 
      name: m.name, 
      contractType: m.contractType 
    })));
    
    const filtered = allMaterials.filter(
      (material) => material.contractType === 'ratio'
    );
    console.log('[AddDerivedMaterialModal] Filtered ratio materials:', filtered);
    return filtered;
  }, [materialsMap]);

  // Format the centralized price date
  const priceAsOfDate = React.useMemo(() => {
    if (!formulasPricesData?.asOfDate) return 'N/A';
    return new Date(formulasPricesData.asOfDate).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }, [formulasPricesData?.asOfDate]);

  // When ratio material is selected, get its current price as the ratio
  React.useEffect(() => {
    if (useRatioMaterial && selectedRatioMaterialId) {
      const ratioMaterial = materialsMap[selectedRatioMaterialId];
      if (ratioMaterial?.currentPrice) {
        setPriceRatio(ratioMaterial.currentPrice.toString());
      }
    }
  }, [useRatioMaterial, selectedRatioMaterialId, materialsMap]);

  const handleSave = () => {
    if (selectedFormulaId && priceRatio) {
      const ratio = parseFloat(priceRatio);
      if (!isNaN(ratio) && ratio > 0) {
        const linkedRatioId = useRatioMaterial && selectedRatioMaterialId ? selectedRatioMaterialId : undefined;
        onSave(selectedFormulaId, ratio, linkedRatioId);
        handleClose();
      }
    }
  };

  const handleClose = () => {
    setSelectedFormulaId('');
    setPriceRatio('1.0');
    setUseRatioMaterial(false);
    setSelectedRatioMaterialId('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Derived Material</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          <TextField
            select
            label="Material"
            value={selectedFormulaId}
            onChange={(e) => setSelectedFormulaId(e.target.value)}
            fullWidth
            required
            helperText="Select the derived material"
          >
            {availableMaterials.length === 0 ? (
              <MenuItem disabled>No available materials</MenuItem>
            ) : (
              availableMaterials.map((material) => (
                <MenuItem key={material.id} value={material.id}>
                  {material.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <FormControlLabel
            control={
              <Switch
                checked={useRatioMaterial}
                onChange={(e) => setUseRatioMaterial(e.target.checked)}
                color="primary"
              />
            }
            label="Link ratio to material price"
          />

          {useRatioMaterial ? (
            <TextField
              select
              label="Ratio Material"
              value={selectedRatioMaterialId}
              onChange={(e) => setSelectedRatioMaterialId(e.target.value)}
              fullWidth
              required
              helperText="Select a ratio material (e.g., Cocoa butter ratio)"
            >
              {ratioMaterials.length === 0 ? (
                <MenuItem disabled>No ratio materials available</MenuItem>
              ) : (
                ratioMaterials.map((material) => (
                  <MenuItem key={material.id} value={material.id}>
                    <span>
                      {material.name} - {material.currentPrice ? Number(material.currentPrice).toFixed(2) : 'N/A'}{' '}
                      <span className="text-xs text-gray-500">(as of {priceAsOfDate})</span>
                    </span>
                  </MenuItem>
                ))
              )}
            </TextField>
          ) : (
            <TextField
              label="Price Ratio"
              type="number"
              value={priceRatio}
              onChange={(e) => setPriceRatio(e.target.value)}
              fullWidth
              required
              inputProps={{ step: 0.01, min: 0.01 }}
              helperText="Base material MT per 1 MT of this material (e.g., 1.22 for cocoa butter)"
            />
          )}

          {useRatioMaterial && selectedRatioMaterialId && (
            <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
              <div>
                Current ratio from {materialsMap[selectedRatioMaterialId]?.name}: <strong>{priceRatio}</strong>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                as of {priceAsOfDate}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!selectedFormulaId || !priceRatio || (useRatioMaterial && !selectedRatioMaterialId)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
